import { type KeyboardEvent, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchesName } from '../lib/name-match.js';
import {
  Combobox,
  type ComboboxHandle,
  ComboboxList,
  ComboboxNotice,
  ComboboxOption,
} from './combobox.js';

// #207: Ersatz für die Nutzer-<select> (Tester, Bearbeiter, Vertretung, Weitergabe …). Verhält
// sich wie der abgelöste <select>: eine Wahl wirkt sofort (onChange), die Leeroption bleibt
// wählbar, Verlassen ohne Wahl ändert nichts. Neu: Tippen filtert tolerant (matchesName — Akzente,
// Wortreihenfolge, ein Tippfehler). Anders als UserPicker im Import (users-step.tsx) hebt Tippen
// die bestehende Auswahl NICHT auf — dort ist die Zuordnung ein HITL-Schritt, hier eine gewöhnliche
// Feldänderung, und ein angefangenes Suchwort darf keine Zuweisung löschen.

export type SearchSelectOption = {
  value: string;
  label: string;
  /** Zusätzliche durchsuchte Texte, z. B. die E-Mail. */
  search?: string[];
};

export function SearchSelect({
  label,
  value,
  onChange,
  options,
  emptyLabel,
  disabled,
  className,
}: {
  label: string;
  /** Gewählter Wert, '' = nichts gewählt. */
  value: string;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  /** Beschriftung der wählbaren Leeroption (Wert ''). Ohne Angabe gibt es keine Leeroption. */
  emptyLabel?: string;
  disabled?: boolean;
  className: string;
}) {
  const { t } = useTranslation();
  const comboRef = useRef<ComboboxHandle>(null);
  const listboxId = useId();
  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';
  // null = nicht am Tippen: das Feld zeigt den gewählten Namen.
  const [query, setQuery] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState(-1);

  const trimmed = query?.trim() ?? '';
  const searching = query !== null && trimmed !== '' && query !== selectedLabel;
  const all: SearchSelectOption[] =
    emptyLabel !== undefined ? [{ value: '', label: emptyLabel }, ...options] : options;
  const shown = searching
    ? options.filter((o) => matchesName(trimmed, o.label, ...(o.search ?? [])))
    : all;
  const currentIndex = shown.findIndex((o) => o.value === value);
  const activeIndex =
    highlighted >= 0
      ? Math.min(highlighted, shown.length - 1)
      : !searching && currentIndex >= 0
        ? currentIndex
        : 0;

  const reset = (): void => {
    setQuery(null);
    setHighlighted(-1);
  };

  const choose = (option: SearchSelectOption): void => {
    reset();
    if (option.value !== value) onChange(option.value);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      reset();
      comboRef.current?.close();
      return;
    }
    if (shown.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(Math.min(activeIndex + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(Math.max(activeIndex - 1, 0));
    } else if (e.key === 'Enter') {
      const target = shown[activeIndex];
      if (target) {
        e.preventDefault();
        choose(target);
        comboRef.current?.close();
      }
    }
  };

  return (
    <Combobox
      ref={comboRef}
      label={label}
      {...(emptyLabel !== undefined ? { placeholder: emptyLabel } : {})}
      value={query ?? selectedLabel}
      onChange={(v) => {
        setQuery(v);
        setHighlighted(-1);
      }}
      onKeyDown={onKeyDown}
      // Text markieren: das erste getippte Zeichen ersetzt den Namen, statt ihn zu ergänzen.
      onFocus={(e) => e.target.select()}
      onBlur={reset}
      {...(disabled !== undefined ? { disabled } : {})}
      listboxId={listboxId}
      {...(shown.length > 0 ? { activeId: `${listboxId}-option-${activeIndex}` } : {})}
      className={className}
    >
      {({ open, close }) =>
        open ? (
          <ComboboxList id={listboxId}>
            {shown.map((o, i) => (
              <ComboboxOption
                key={o.value}
                id={`${listboxId}-option-${i}`}
                selected={i === activeIndex}
                onSelect={() => {
                  choose(o);
                  close();
                }}
                className={`block w-full px-2 py-1 text-left text-text-body hover:bg-brand-accent/10 ${
                  i === activeIndex ? 'bg-brand-accent/10' : ''
                }`}
              >
                {o.label}
              </ComboboxOption>
            ))}
            {searching && shown.length === 0 && (
              <ComboboxNotice>{t('catalog.search.noResults', { query: trimmed })}</ComboboxNotice>
            )}
          </ComboboxList>
        ) : null
      }
    </Combobox>
  );
}
